import { useEffect, useMemo, useState } from "react";
import { lessonCategories, lessons } from "@/mocks/lessons";
import type { Lesson } from "@/pages/home/types";

const formatIcon: Record<string, string> = {
  视频: "ri-play-circle-line",
  图文: "ri-article-line",
};

export default function LessonExplorer() {
  const [activeCategory, setActiveCategory] = useState("全部");
  const [keyword, setKeyword] = useState("");
  const [selected, setSelected] = useState<Lesson | null>(null);

  const trimmedKeyword = keyword.trim().toLowerCase();

  const filtered = useMemo(() => {
    const source = lessons as Lesson[];
    return source.filter((lesson) => {
      const matchCategory =
        activeCategory === "全部" || lesson.category === activeCategory;
      const matchKeyword =
        trimmedKeyword.length === 0 ||
        lesson.title.toLowerCase().includes(trimmedKeyword) ||
        lesson.summary.toLowerCase().includes(trimmedKeyword) ||
        lesson.no.toLowerCase().includes(trimmedKeyword) ||
        lesson.category.toLowerCase().includes(trimmedKeyword) ||
        lesson.tags.some((tag) => tag.toLowerCase().includes(trimmedKeyword));
      return matchCategory && matchKeyword;
    });
  }, [activeCategory, trimmedKeyword]);

  useEffect(() => {
    if (!selected) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSelected(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  return (
    <section id="lessons" className="w-full bg-background-50 py-16 md:py-24">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
        <div className="max-w-3xl">
          <span className="text-xs md:text-sm font-label font-medium tracking-widest text-accent-700 uppercase">
            Lesson navigator
          </span>
          <h2 className="mt-3 font-heading font-bold text-2xl md:text-4xl text-foreground-950 leading-snug">
            课程导航：按阶段筛选，或搜索关键词
          </h2>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700 font-body">
            下面是逐课整理的教学目录。先按阶段筛选，缩小范围；再用关键词搜索课次、标题或标签，
            点开任意条目即可查看这一课讲什么、适合谁看、有哪些要点。
          </p>
        </div>

        <div className="mt-8 flex flex-col lg:flex-row lg:items-center gap-4 lg:justify-between">
          <div className="flex flex-wrap gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
            {lessonCategories.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full text-xs md:text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                  activeCategory === category
                    ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                    : "text-foreground-700 hover:bg-background-200/70"
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-80 shrink-0">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索课程、课次或标签…"
              className="w-full pl-9 pr-9 py-2.5 rounded-md bg-background-100 border border-background-200/70 text-sm font-body text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400/60"
            />
            {keyword.length > 0 && (
              <button
                type="button"
                onClick={() => setKeyword("")}
                aria-label="清空搜索"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-foreground-500 hover:text-foreground-800 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
        </div>

        <p className="mt-4 text-xs md:text-sm text-foreground-500 font-label">
          共找到 <span className="text-primary-600 font-medium">{filtered.length}</span>{" "}
          个条目
          {activeCategory !== "全部" && <> · 当前阶段「{activeCategory}」</>}
        </p>

        {filtered.length === 0 ? (
          <div className="mt-8 rounded-lg bg-background-100 border border-background-200/70 py-16 text-center">
            <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-200/70 text-foreground-600 text-2xl">
              <i className="ri-search-eye-line"></i>
            </span>
            <p className="mt-4 font-heading font-semibold text-foreground-900">
              没有匹配的课程
            </p>
            <p className="mt-2 text-sm text-foreground-600 font-body">
              换一个关键词，或点击「全部」查看完整目录。
            </p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setActiveCategory("全部");
              }}
              className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((lesson) => (
              <article
                key={lesson.id}
                className="group flex flex-col rounded-lg bg-background-100 border border-background-200/70 p-5 transition-colors hover:border-primary-300"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-label">
                    {lesson.no}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-foreground-600 font-label">
                    <i className={formatIcon[lesson.format] ?? "ri-file-line"}></i>
                    {lesson.format}
                  </span>
                </div>

                <h3 className="mt-3 font-heading font-semibold text-base md:text-lg text-foreground-950 leading-snug">
                  {lesson.title}
                </h3>
                <p className="mt-2 flex-1 text-xs md:text-sm leading-relaxed text-foreground-600 font-body">
                  {lesson.summary}
                </p>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {lesson.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 rounded-md bg-background-200/70 text-foreground-700 text-xs font-label"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-background-200/70 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 text-xs text-foreground-600 font-label">
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-time-line"></i>
                      {lesson.duration}
                    </span>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-bar-chart-line"></i>
                      {lesson.level}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelected(lesson)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary-100 text-primary-800 text-xs font-label font-medium hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    查看详情
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-label={`${selected.title} 详情`}
        >
          <button
            type="button"
            aria-label="关闭详情"
            onClick={() => setSelected(null)}
            className="absolute inset-0 bg-foreground-950/60 cursor-pointer"
          ></button>

          <div className="relative z-10 w-full sm:max-w-lg max-h-[88vh] overflow-y-auto rounded-t-lg sm:rounded-lg bg-background-50 border border-background-200/70">
            <div className="sticky top-0 flex items-start justify-between gap-4 px-5 md:px-6 py-4 bg-background-50 border-b border-background-200/70">
              <div>
                <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-label">
                  {selected.no} · {selected.category}
                </span>
                <h3 className="mt-2 font-heading font-bold text-lg md:text-xl text-foreground-950 leading-snug">
                  {selected.title}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                aria-label="关闭"
                className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="px-5 md:px-6 py-5">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-md bg-background-100 px-3 py-2.5">
                  <p className="text-xs text-foreground-500 font-label">时长</p>
                  <p className="mt-0.5 text-sm font-medium text-foreground-900 font-label">
                    {selected.duration}
                  </p>
                </div>
                <div className="rounded-md bg-background-100 px-3 py-2.5">
                  <p className="text-xs text-foreground-500 font-label">形式</p>
                  <p className="mt-0.5 text-sm font-medium text-foreground-900 font-label">
                    {selected.format}
                  </p>
                </div>
                <div className="rounded-md bg-background-100 px-3 py-2.5">
                  <p className="text-xs text-foreground-500 font-label">难度</p>
                  <p className="mt-0.5 text-sm font-medium text-foreground-900 font-label">
                    {selected.level}
                  </p>
                </div>
              </div>

              <p className="mt-5 text-sm leading-relaxed text-foreground-700 font-body">
                {selected.summary}
              </p>

              <h4 className="mt-6 font-heading font-semibold text-sm md:text-base text-foreground-950">
                <a href="#lessons" className="inline-flex items-center gap-1.5">
                  <i className="ri-bookmark-3-line text-accent-600"></i>
                  本课要点
                </a>
              </h4>
              <ul className="mt-3 space-y-2.5">
                {selected.points.map((point) => (
                  <li
                    key={point}
                    className="flex items-start gap-2.5 text-sm text-foreground-700 font-body"
                  >
                    <i className="ri-checkbox-circle-fill text-primary-500 mt-0.5"></i>
                    <span className="leading-relaxed">{point}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 flex flex-wrap gap-1.5">
                {selected.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-label"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}